import { skipToken } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { Target } from "lucide-react"
import { useTranslation } from "react-i18next"
import { z } from "zod"
import { StepNavigation } from "@/components/forms/step-navigation"
import { StepRenderer } from "@/components/forms/step-renderer"
import { useStepNavigation } from "@/components/forms/use-step-navigation"
import { Owners } from "@/components/owners"
import { Page } from "@/components/page"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { EstateTotals } from "@/components/ui/estate-totals"
import { Typography } from "@/components/ui/typography"
import { STEPS } from "@/constants/steps"
import { trpc } from "@/lib/trpc"

type StepId = (typeof STEPS)[number]["id"]

// Define search params schema for step navigation with proper validation
// Use .optional().catch() to handle removed steps (like "goals") gracefully by defaulting to "intro"
const searchSchema = z.object({
  currentStep: z
    .enum([...STEPS.map((s) => s.id)] as [StepId, ...StepId[]])
    .optional()
    .catch("intro"),
})

export const Route = createFileRoute("/_authed/_professional/professional/guided-forms/$estateId")({
  validateSearch: (search) => searchSchema.parse(search),
  component: RouteComponent,
})

function RouteComponent() {
  const { estateId } = Route.useParams()
  const search = Route.useSearch()
  const { t } = useTranslation()

  const {
    data: guidedForms,
    isLoading: guidedFormsLoading,
    error: guidedFormsError,
  } = trpc.listGuidedForms.useQuery({
    estateId: estateId ?? skipToken,
  })

  const {
    data: owners = [],
    isLoading: ownersLoading,
    error: ownersError,
  } = trpc.estates.getEstateOwners.useQuery({
    estateId: estateId ?? skipToken,
  })

  const stepNavigation = useStepNavigation({ currentStep: search.currentStep ?? "intro" })

  const isLoading = ownersLoading || guidedFormsLoading
  const error = ownersError || guidedFormsError

  if (isLoading) {
    return (
      <Page
        headerSlot={
          <div className="flex items-center space-x-4">
            <Typography as="h1" size="2xl">
              {t("pages.reports.estate.loading.title")}
            </Typography>
          </div>
        }
      >
        <div>{t("pages.reports.estate.loading.content")}</div>
      </Page>
    )
  }

  if (error || !guidedForms) {
    return (
      <Page
        headerSlot={
          <div className="flex items-center space-x-4">
            <Typography as="h1" size="2xl">
              {t("pages.reports.estate.error.title")}
            </Typography>
          </div>
        }
      >
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="font-medium text-red-800">{t("pages.reports.estate.error.message")}</p>
          <p className="text-red-700">
            {error?.message || t("pages.reports.estate.error.estateNotFound")}
          </p>
        </div>
      </Page>
    )
  }
  return (
    <Page
      headerSlot={
        <Typography as="h1" size="2xl">
          Guided forms
        </Typography>
      }
    >
      <div className="space-y-6">
        <Card className="relative">
          <CardContent>
            <div className="space-y-12">
              <Owners owners={owners} estateId={estateId} align="left">
                <Button variant="link" asChild className="px-0">
                  <Link to="/professional/goals/$estateId" params={{ estateId }}>
                    <Target className="size-4 mr-2" />
                    Go to Goals
                  </Link>
                </Button>
              </Owners>
            </div>
            <div className="flex justify-center lg:absolute right-0 top-0 pt-6 lg:pr-6">
              <EstateTotals estateId={estateId} className="" />
            </div>
            <div className="mt-6 space-y-6">
              <StepNavigation {...stepNavigation} />
              <StepRenderer
                currentStep={stepNavigation.currentStep}
                estateId={estateId}
                isUserLoading={false}
                isUserError={false}
                onNext={stepNavigation.goToNextStep}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </Page>
  )
}
